import { useEffect, type FC, useState } from 'react';
import AlertTriangleIcon from '@untitled-ui/icons-react/build/esm/AlertTriangle';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import SvgIcon from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';
import { RouterLink } from 'src/components/router-link';
import { paths } from 'src/paths';
import { ArrowLeftIcon } from '@mui/x-date-pickers';
import { Card, Link } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { deactivatePatientAction, resetDeactivatePatientAction } from 'src/redux/actions/patientAction';
import { useLocation, useNavigate } from 'react-router';
import SimpleSnackbar from 'src/pages/details-page/sub-components/snackBar';

type Props = {
  value?: boolean;
}

export const DeleteModal = (props: Props) => {
  const { value } = props;
  const { state } = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { patients, patientDetails } = paths;
  const active = state.value;;
  const deactivatePatientData = useSelector((state: any) => state?.deactivatePatientReducer);
  const [patientError, setPatientError] = useState(false);
  const getPatientId = useSelector((state: any) => state?.getPatientIdReducer);
  const [activate, setActivate] = useState(Boolean);

  const onDeactivate = () => {
    dispatch(deactivatePatientAction({ patientId: getPatientId, active: active }));
  };

  const onCancel = () => {
    navigate(`${patients}/${patientDetails}`);
  };

  useEffect(() => {
    if (!deactivatePatientData.isPatientDeactivated && deactivatePatientData.response === null)
      return;
    if (!deactivatePatientData.isPatientDeactivated && deactivatePatientData.response !== null) {
      setPatientError(true);
      setTimeout(() => {
        dispatch(resetDeactivatePatientAction());
      }, 3000);

      return;
    }
    if (deactivatePatientData?.isPatientEdited) setPatientError(false);
    setTimeout(() => {
      dispatch(resetDeactivatePatientAction());
      navigate(`${patients}/${patientDetails}`);
    }, 3000);
    return;
  }, [deactivatePatientData]);

  return (
    <>
      <Box
        maxWidth={'xl'}
        sx={{
          position: 'fixed',
          width: '100%',
          height: '100%',
          // background: 'rgba(255, 255, 255, 0.87)', /* Semi-transparent background overlay */
          // zIndex: '1000',
          margin: '0px 55px',
          padding: '0px 30px',
        }}
      >
        <Stack
          sx={{
            background: '#FFF',
            padding: '20px 0px',
          }}
        >
          <div>
            <Link
              color="text.primary"
              component={RouterLink}
              href={paths.patients}
              sx={{
                alignItems: 'center',
                display: 'inline-flex',
              }}
              underline="hover"
            >
              <SvgIcon sx={{ mr: 1 }}>
                <ArrowLeftIcon />
              </SvgIcon>
              <Typography variant="subtitle2">Back to Patient List</Typography>
            </Link>
          </div>
        </Stack>
        <Card>
          <Box padding={'30px'}>
            <Typography variant="h6">
              {active === true ? 'Activating' : 'Inactivating'} Patient Account
            </Typography>
          </Box>
          <Box
            sx={{
              backgroundColor: (theme) =>
                theme.palette.mode === 'dark' ? 'neutral.800' : 'neutral.100',
              p: 3,
            }}
          >
            <Container maxWidth="md">
              <Paper elevation={12}>
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{
                    display: 'flex',
                    p: 3,
                  }}
                >
                  <Avatar
                    sx={{
                      backgroundColor: 'error.lightest',
                      color: 'error.main',
                    }}
                  >
                    <SvgIcon>
                      <AlertTriangleIcon />
                    </SvgIcon>
                  </Avatar>
                  <div>
                    <Typography variant="h5">
                      {active == true ? 'Activate' : 'Deactivate'} account
                    </Typography>
                    <Typography
                      color="text.secondary"
                      sx={{ mt: 1 }}
                      variant="body2"
                    >
                      {active === true
                        ? 'Are you sure you want to activate this account? Activating can allow the access for end user.'
                        : 'Are you sure you want to deactivate this account? Deactivating can interrupt the access for end user.'}
                    </Typography>
                  </div>
                </Stack>
                {!deactivatePatientData?.isPatientDeactivated && (
                  <Typography
                    padding={'0px 20px'}
                    variant="h6"
                    color="red"
                  >
                    {patientError}
                  </Typography>
                )}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    pb: 3,
                    px: 3,
                  }}
                >
                  <Button
                    color="inherit"
                    sx={{ mr: 2 }}
                    onClick={onCancel}
                  >
                    Cancel
                  </Button>
                  <Button
                    sx={{
                      backgroundColor: 'error.main',
                      '&:hover': {
                        backgroundColor: 'error.dark',
                      },
                    }}
                    variant="contained"
                    onClick={onDeactivate}
                  >
                    {active == true ? 'Activate' : 'Deactivate'}
                  </Button>
                </Box>
              </Paper>
            </Container>
          </Box>
        </Card>
        {!!deactivatePatientData?.response && (
          <SimpleSnackbar
            message={deactivatePatientData?.response?.message}
            isError={patientError}
          />
        )}
      </Box>
    </>
  );
}
